
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const PartnershipVisuals = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 6);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">Partnership Builder</span>
                    </div>
                </div>
                <div className="w-12" />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Basics key="step1" />}
                    {activeStep === 1 && <Step2_Details key="step2" />}
                    {activeStep === 2 && <Step3_QA key="step3" />}
                    {activeStep === 3 && <Step4_Outline key="step4" />}
                    {activeStep === 4 && <Step5_Theme key="step5" />}
                    {activeStep === 5 && <Step6_Edit key="step6" />}
                </AnimatePresence>

                {/* Progressive Step Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Company Basics
const Step1_Basics = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center gap-4"
    >
        <div className="space-y-3 px-2">
            <div className="space-y-1">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Our Company</label>
                <div className="flex items-center gap-2 p-2 border border-gray-100 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-[10px] font-bold">A</div>
                    <span className="text-sm font-bold text-gray-900">Acme Innovations</span>
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Potential Partner</label>
                <div className="flex items-center gap-2 p-2 border border-blue-100 rounded-lg bg-white shadow-sm ring-1 ring-blue-50">
                    <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center text-white text-[10px] font-bold">G</div>
                    <span className="text-sm font-bold text-gray-900">Global Tech Corp</span>
                </div>
            </div>
        </div>
    </motion.div>
);

// Step 2: Proposal Details
const Step2_Details = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center"
    >
        <div className="w-full bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-900">Partnership Goal</span>
                <span className="text-[9px] text-gray-400 font-mono">150 chars</span>
            </div>
            <div className="text-sm text-gray-600 leading-relaxed font-medium">
                Proposing a strategic alliance to integrate Acme's AI engine into Global Tech's cloud platform...
                <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block w-0.5 h-4 bg-blue-600 align-middle ml-0.5"
                />
            </div>
        </div>
    </motion.div>
);

// Step 3: Q&A
const Step3_QA = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-3 pt-4"
    >
        {/* AI Question */}
        <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-blue-50 border border-blue-100 rounded-xl p-3 relative"
        >
            <div className="absolute -top-3 left-4 bg-white text-blue-600 px-2 py-0.5 rounded-full text-[9px] font-bold border border-blue-100 shadow-sm">AI Coach</div>
            <div className="text-xs text-blue-900 font-medium">What is the primary benefit for Global Tech?</div>
        </motion.div>

        {/* User Answer Selection */}
        <div className="space-y-2 mt-2">
            {[
                "Revenue Share",
                "Market Expansion",
                "Product Differentiation"
            ].map((opt, i) => (
                <motion.div
                    key={i}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + (i * 0.1) }}
                    className={`p-2.5 rounded-lg border text-xs font-bold cursor-pointer flex justify-between items-center ${i === 1 ? 'bg-blue-600 border-blue-600 text-white shadow-md' : 'bg-white border-gray-100 text-gray-600 hover:bg-gray-50'}`}
                >
                    {opt}
                    {i === 1 && <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 4: Outline
const Step4_Outline = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff] pl-2"
    >
        <div className="border-l-2 border-gray-100 pl-4 space-y-4 py-2">
            {[
                { title: "Executive Summary", status: "Ready" },
                { title: "Market Opportunity", status: "Ready" },
                { title: "Strategic Fit", status: "Drafting..." },
                { title: "Implementation Plan", status: "Pending" }
            ].map((item, i) => (
                <motion.div
                    key={i}
                    initial={{ x: 10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.15 }}
                    className="relative"
                >
                    <div className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 ${item.status === 'Drafting...' ? 'bg-blue-500 border-blue-100 animate-pulse' : 'bg-white border-gray-200'}`} />
                    <div className="text-sm font-bold text-gray-900">{item.title}</div>
                    <div className={`text-[9px] font-medium ${item.status === 'Drafting...' ? 'text-blue-500' : 'text-gray-400'}`}>{item.status}</div>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

// Step 5: Theme
const Step5_Theme = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-4"
    >
        <div className="flex justify-between items-center text-xs font-bold text-gray-900 border-b border-gray-50 pb-2">
            <span>Select Style</span>
            <div className="px-2 py-0.5 bg-gray-100 rounded text-[9px] text-gray-500">Corporate</div>
        </div>

        <div className="flex gap-4 h-full overflow-hidden">
            {/* Preview Card */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex-1 rounded-xl border border-gray-200 bg-white shadow-xl relative p-4 flex flex-col"
            >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg mb-4 opacity-10" />
                <div className="text-lg font-serif font-bold text-gray-900 leading-tight mb-2">Strategic<br />Partnership</div>
                <div className="w-8 h-1 bg-blue-600 mb-4" />
                <div className="space-y-1 mt-auto">
                    <div className="h-1 w-full bg-gray-100 rounded" />
                    <div className="h-1 w-2/3 bg-gray-100 rounded" />
                </div>

                {/* Overlay Check */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                </div>
            </motion.div>

            {/* Sidebar Options */}
            <div className="w-12 flex flex-col gap-2">
                {[1, 2, 3].map(i => (
                    <div key={i} className={`w-full aspect-square rounded-lg border ${i === 1 ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-white'}`} />
                ))}
            </div>
        </div>
    </motion.div>
);

// Step 6: Edit with Advanced AI Tools
const Step6_Edit = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-blue-50/20 relative"
    >
        {/* Advanced Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-white/80 border-b border-blue-100 backdrop-blur-sm">
            <div className="flex gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-blue-600 shadow-sm shadow-blue-200">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    <span className="text-[9px] font-bold text-white">Smart Edit</span>
                </div>
                <div className="w-px h-4 bg-blue-200 my-auto" />
                <div className="p-1 rounded hover:bg-blue-50 text-blue-400"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg></div>
                <div className="p-1 rounded hover:bg-blue-50 text-blue-400"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg></div>
            </div>
            <div className="text-[8px] font-bold text-blue-300 uppercase tracking-wider">AI Powered</div>
        </div>

        <div className="flex-1 p-3 overflow-hidden flex gap-3 relative">
            {/* Slide Canvas */}
            <motion.div
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex-1 bg-white rounded-lg shadow-xl shadow-blue-900/5 border border-blue-100 p-4 relative overflow-hidden group"
            >
                <div className="absolute top-0 right-0 p-2 opacity-50"><div className="w-8 h-8 rounded-full bg-blue-50"></div></div>

                <div className="space-y-3 relative z-10">
                    <div>
                        <motion.div initial={{ width: 0 }} animate={{ width: "80%" }} transition={{ delay: 0.2 }} className="h-4 bg-slate-900 rounded mb-1.5" />
                        <motion.div initial={{ width: 0 }} animate={{ width: "40%" }} transition={{ delay: 0.3 }} className="h-1.5 bg-blue-500 rounded-full" />
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-4">
                        {[1, 2, 3].map(i => (
                            <motion.div
                                key={i}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.4 + (i * 0.1) }}
                                className="bg-blue-50/50 rounded-lg p-2 border border-blue-50 h-16 flex flex-col justify-between group-hover:bg-blue-50 transition-colors"
                            >
                                <div className="w-4 h-4 rounded bg-blue-200" />
                                <div className="space-y-0.5">
                                    <div className="h-1 w-full bg-blue-200 rounded-full" />
                                    <div className="h-1 w-2/3 bg-blue-100 rounded-full" />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* AI Suggestion Pop-up */}
                <motion.div
                    initial={{ y: 20, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ delay: 1, type: "spring" }}
                    className="absolute bottom-3 left-3 right-3 bg-slate-900/95 backdrop-blur text-white p-2.5 rounded-lg border border-slate-700 shadow-2xl flex items-center justify-between"
                >
                    <div className="flex items-center gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        </div>
                        <div>
                            <div className="text-[9px] font-bold text-gray-300 uppercase tracking-wide">Optimization</div>
                            <div className="text-[10px] font-medium text-white">Make tone more persuasive?</div>
                        </div>
                    </div>
                    <button className="bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-bold px-2 py-1 rounded transition-colors">Apply Fix</button>
                </motion.div>
            </motion.div>
        </div>
    </motion.div>
);
